import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Drawer, Form, Input, Select, Button, Space, Modal, Upload } from 'antd';
import { CloseCircle, CallAdd } from 'iconsax-react';
import { UploadOutlined } from '@ant-design/icons';

import { getServiceCallList, getProblemList, getEquipmentSearch, GetSubProblemList, saveServiceCall } from "../../redux/service-call/serviceCallActions";
import { uniqueValues } from '../../utils/utils';

const { TextArea } = Input;

const ServiceCallDrawer = ({ isAddButtonEnabled, showServiceCallDrawer, handleCloseServiceCallDrawer, mode, serviceCallDetails }) => {
    const [spinStatus, setSpinStatus] = useState(false);
    const [fileList, setFileList] = useState([]);

    const { equipmentList, problemList, subProblemList } = useSelector((state) => (state.serviceCall));

    const serviceCallRef = useRef();

    useEffect(async () => {
        await getProblemList();
        await GetSubProblemList();
        await getEquipmentSearch({ cardCode: localStorage.getItem('cardCode'), equipment: "" });
    }, []);

    useEffect(() => {
        if (mode === "fromDashboard") {
            setTimeout(() => {
                serviceCallRef?.current?.setFieldsValue({
                    equipment: serviceCallDetails?.equipId ?? undefined,
                });
            }, 10);
        };
    }, [showServiceCallDrawer]);

    useEffect(() => {
        if (mode === "view") {
            setTimeout(() => {
                serviceCallRef?.current?.setFieldsValue({
                    equipment: serviceCallDetails?.internalSn ?? undefined,
                    priority: serviceCallDetails?.priority ?? undefined,
                    problemType: serviceCallDetails?.problemTypeId ?? undefined,
                    subProblemType: serviceCallDetails?.subProblemTypeId ?? undefined,
                    subject: serviceCallDetails?.subject ?? undefined,
                    description: serviceCallDetails?.description ?? undefined,
                });
            }, 10);
        };
    }, [showServiceCallDrawer]);

    const renderEquipmentDropdown = useCallback(() => {
        if (equipmentList?.length > 0) {
            const uniqueOptions = uniqueValues(equipmentList, "serialNum", "equipmentId");
            const formattedOptions = uniqueOptions?.map((eachOption, index) => ({ label: eachOption.props.children, value: eachOption.props.value, key: index }));
            return formattedOptions;
        };
    }, [equipmentList]);

    const renderProblemDropdown = useCallback(() => {
        if (problemList?.length > 0) {
            const uniqueOptions = uniqueValues(problemList, "prblmId", "prblmType");
            const formattedOptions = uniqueOptions?.map((eachOption, index) => ({ label: eachOption.props.children, value: eachOption.props.value, key: index }));
            return formattedOptions;
        };
    }, [problemList]);

    const renderSubProblemDropdown = useCallback(() => {
        if (subProblemList?.length > 0) {
            const uniqueOptions = uniqueValues(subProblemList, "proSubTyId", "name");
            const formattedOptions = uniqueOptions?.map((eachOption, index) => ({ label: eachOption.props.children, value: eachOption.props.value, key: index }));
            return formattedOptions;
        };
    }, [subProblemList]);

    const attachFile = (file) => {
        var newFile = fileList;
        fileList.push(file);
        setFileList(newFile);
    };

    const removeFile = (file) => {
        const index = fileList.indexOf(file);
        const newFileList = fileList.slice();
        newFileList.splice(index, 1);
        setFileList(newFileList);
    };

    const handleCreateServiceCall = async () => {
        try {
            await serviceCallRef?.current?.validateFields();
        }
        catch {
            const fieldNames = ["equipment", "priority", "problemType", "subProblemType", "subject"];

            for (let i = 0; i < fieldNames.length; i++) {
                const eachFiedName = fieldNames[i];
                const errorField = serviceCallRef?.current?.getFieldError(eachFiedName);

                if (errorField && errorField.length > 0) {
                    serviceCallRef?.current?.scrollToField(eachFiedName, {
                        behavior: "smooth",
                        block: "center",
                    });
                    return;
                };
            };
        };

        const formFieldValues = serviceCallRef?.current?.getFieldsValue();

        const formData = new FormData();
        const matchedEquipment = equipmentList?.find((eachEquipment) => (eachEquipment.serialNum === formFieldValues?.equipment));

        formData.append('serialNum', matchedEquipment?.serialNum);
        formData.append('itemCode', matchedEquipment?.itemCode);
        formData.append('manufSerialNumber', matchedEquipment?.manuSerialNum);
        formData.append('problemId', formFieldValues?.problemType);
        formData.append('subProblemId', formFieldValues?.subProblemType);
        formData.append('priority', formFieldValues?.priority);
        formData.append('subject', formFieldValues?.subject);
        formData.append('description', formFieldValues?.description ?? "");
        formData.append('customer', localStorage?.getItem("cardCode"));
        fileList.forEach((file) => {
            formData.append('files', file);
        });
        console.log(formData)
        setSpinStatus(true);
        const response = await saveServiceCall(formData);
        setSpinStatus(false);

        if (response?.result) {
            Modal.success({ title: 'Success', content: response.msg });
            handleCloseServiceCallDrawer();
            await getServiceCallList({ cardCode: localStorage.getItem('cardCode'), search: "", sIndex: 0, limit: 1000 });
        }
        else {
            Modal.error({ title: 'Error', content: response.msg });
        };
    };

    return (
        <Drawer title={mode === "add" || mode === "fromDashboard" ? "Create Service Call" : "View Service Call"} width={460} visible={showServiceCallDrawer}
            onClose={handleCloseServiceCallDrawer} destroyOnClose={true} footer={
                <Space className='hp-w-100' style={{ justifyContent: 'space-between' }}>
                    <Button onClick={handleCloseServiceCallDrawer}><CloseCircle size={16} className='hp-mr-8' />&nbsp;Cancel</Button>
                    <Button form="serviceCallForm" type="primary" loading={spinStatus} onClick={handleCreateServiceCall} hidden={mode === "view"}>
                        <CallAdd size={16} className='hp-mr-8' />&nbsp;Create Call
                    </Button>
                </Space>}
        >
            <Form ref={serviceCallRef} layout="vertical">
                <Form.Item name="equipment" label="Equipment" rules={[{ required: true, message: "" }]}>
                    <Select placeholder={mode === "add" || mode === "fromDashboard" ? "Select equipment" : undefined} options={renderEquipmentDropdown()} showSearch filterOption optionFilterProp='label' disabled={mode === "view" || mode === "fromDashboard"} />
                </Form.Item>
                <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "" }]}>
                    <Select placeholder={mode === "add" || mode === "fromDashboard" ? "Select priority" : undefined} options={[{ label: "Low", value: "L" }, { label: "Medium", value: "M" }, { label: "High", value: "H" }]} disabled={mode === "view"} />
                </Form.Item>
                <Form.Item name="problemType" label="Problem Type" rules={[{ required: true, message: "" }]}>
                    <Select placeholder={mode === "add" || mode === "fromDashboard" ? "Select problem type" : undefined} options={renderProblemDropdown()} showSearch filterOption optionFilterProp='label' disabled={mode === "view"} />
                </Form.Item>
                <Form.Item name="subProblemType" label="Sub Problem Type" rules={[{ required: true, message: "" }]}>
                    <Select placeholder={mode === "add" || mode === "fromDashboard" ? "Select sub-problem type" : undefined} options={renderSubProblemDropdown()} showSearch filterOption optionFilterProp='label' disabled={mode === "view"} />
                </Form.Item>
                <Form.Item name="subject" label="Subject" rules={[{ required: true, message: "" }]}>
                    <TextArea rows={1} placeholder={mode === "add" || mode === "fromDashboard" ? "Enter subject" : undefined} disabled={mode === "view"} />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <TextArea rows={3} placeholder={mode === "add" || mode === "fromDashboard" ? "Describe the problem" : undefined} disabled={mode === "view"} />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <p>Email Preview</p>
                </Form.Item>
                <Form.Item label="Attachment" name={"attachment"}>
                    <Upload beforeUpload={(file) => { attachFile(file);  return false;  }} onRemove={(file) => { removeFile(file); }} multiple={false} disabled={mode === "view"}>
                        <Button icon={<UploadOutlined />}>Upload Files</Button>
                    </Upload>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default ServiceCallDrawer;